import {
  type KeyboardEvent,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  blogHubTagLabel,
  blogHubUniqueTags,
  blogTagHref,
  getHubTagsScrollBehavior,
  getHubTagsScrollState,
} from '@/lib/entities/blog';

import { ArrowIcon } from '@/lib/shared/icons';

import { clsx } from 'clsx';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';

import styles from '../BlogHubPage.module.scss';

type BlogHubTagsProps = {
  tags: string[];
  activeTag?: string | null;
};

export const BlogHubTags = ({ tags, activeTag = null }: BlogHubTagsProps) => {
  const { t } = useTranslation('index', { keyPrefix: 'blog' });
  const unique = useMemo(() => blogHubUniqueTags(tags), [tags]);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const scroller = scrollerRef.current;
    const list = listRef.current;

    if (!scroller || !list) {
      return;
    }

    const nextState = getHubTagsScrollState(
      scroller.scrollLeft,
      scroller.clientWidth,
      scroller.scrollWidth,
      list.scrollWidth
    );
    setIsOverflowing(nextState.isOverflowing);
    setCanScrollLeft(nextState.canScrollLeft);
    setCanScrollRight(nextState.canScrollRight);
  }, []);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const list = listRef.current;

    if (!scroller || !list) {
      return;
    }

    updateScrollState();
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(scroller);
    observer.observe(list);
    scroller.addEventListener('scroll', updateScrollState, { passive: true });

    let isCancelled = false;
    document.fonts?.ready.then(() => {
      if (!isCancelled) {
        updateScrollState();
      }
    });

    return () => {
      isCancelled = true;
      observer.disconnect();
      scroller.removeEventListener('scroll', updateScrollState);
    };
  }, [unique, updateScrollState]);

  const handleScrollBy = (direction: -1 | 1) => {
    const scroller = scrollerRef.current;

    if (!scroller) {
      return;
    }

    scroller.scrollBy({
      left: direction * Math.max(scroller.clientWidth * 0.7, 160),
      behavior: getHubTagsScrollBehavior(),
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }

    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const canScroll = direction === 1 ? canScrollRight : canScrollLeft;

    if (!canScroll) {
      return;
    }

    event.preventDefault();
    handleScrollBy(direction);
  };

  if (unique.length === 0) {
    return null;
  }

  return (
    <nav
      className={styles.hub__tags}
      aria-label={t('tagsLabel')}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={scrollerRef}
        className={clsx(
          styles.hub__tagsTrack,
          isOverflowing && styles.hub__tagsTrackOverflow
        )}
      >
        <div ref={listRef} className={styles.hub__tagsList}>
          {unique.map((tag) => (
            <Link
              key={tag}
              href={blogTagHref(tag)}
              className={styles.hub__tag}
              aria-current={tag === activeTag ? 'page' : undefined}
            >
              {blogHubTagLabel(tag)}
            </Link>
          ))}
        </div>
      </div>
      {canScrollLeft ? (
        <>
          <div className={styles.hub__tagsFadePrev} aria-hidden="true" />
          <button
            type="button"
            className={styles.hub__tagsPrev}
            aria-label={t('tagsPrev')}
            onClick={() => handleScrollBy(-1)}
          >
            <ArrowIcon className={styles.hub__tagsArrowIcon} />
          </button>
        </>
      ) : null}
      {canScrollRight ? (
        <>
          <div className={styles.hub__tagsFadeNext} aria-hidden="true" />
          <button
            type="button"
            className={styles.hub__tagsNext}
            aria-label={t('tagsNext')}
            onClick={() => handleScrollBy(1)}
          >
            <ArrowIcon isRight className={styles.hub__tagsArrowIcon} />
          </button>
        </>
      ) : null}
    </nav>
  );
};
